import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Kanban, Plus, FolderSimple, CalendarBlank, Users, Target, CaretRight,
  CheckCircle, Circle, XCircle, Funnel, UploadSimple
} from '@phosphor-icons/react';
import ImportModal from '../../components/ImportModal';

const STATUS_BADGE = {
  active: { bg: '#dcfce7', color: '#166534', label: 'Active' },
  paused: { bg: '#fef3c7', color: '#92400e', label: 'Paused' },
  completed: { bg: '#dbeafe', color: '#1e40af', label: 'Completed' },
  archived: { bg: '#f1f5f9', color: '#475569', label: 'Archived' },
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [filter, setFilter] = useState('');
  const [form, setForm] = useState({ title: '', description: '', tags: '', start_date: '', target_end_date: '' });
  const [teamInput, setTeamInput] = useState('');

  const load = useCallback(async () => {
    try {
      const p = await api.getProjects(filter || undefined);
      setProjects(p);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      const team = teamInput ? teamInput.split(',').map(t => {
        const parts = t.trim().split(':');
        return { name: parts[0]?.trim() || '', role: parts[1]?.trim() || 'contributor' };
      }) : [];
      const project = await api.createProject({
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [],
        team,
      });
      toast.success('Project created');
      setShowCreate(false);
      setForm({ title: '', description: '', tags: '', start_date: '', target_end_date: '' });
      setTeamInput('');
      navigate(`/projects/${project.id}`);
    } catch (e) { toast.error('Failed to create project'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="projects-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Projects</h1>
          <p className="text-slate-400 mt-1">Manage research projects with milestones, benchmarks, and pipeline tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={() => setShowImport(true)} data-testid="import-projects-btn">
            <UploadSimple size={16} /> Import
          </Button>
          <Button className="btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)} data-testid="create-project-btn">
            <Plus size={16} /> New Project
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5">
        <Funnel size={16} className="text-slate-400" />
        {['', 'active', 'paused', 'completed', 'archived'].map(s => (
          <button key={s} className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === s ? 'bg-indigo-900/40 text-indigo-300 font-medium' : 'text-slate-400 hover:bg-slate-100'}`}
            onClick={() => setFilter(s)} data-testid={`filter-${s || 'all'}`}>
            {s ? STATUS_BADGE[s]?.label : 'All'}
          </button>
        ))}
      </div>

      {/* Project Grid */}
      {projects.length === 0 ? (
        <div className="card-flat p-12 text-center text-slate-400">
          <Kanban size={48} className="mx-auto mb-3 opacity-40" />
          <h3 className="text-lg font-medium mb-1" style={{ color: '#F1F5F9' }}>No projects yet</h3>
          <p className="text-sm">Create your first research project to start tracking milestones and tasks.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map(p => {
            const badge = STATUS_BADGE[p.status] || STATUS_BADGE.active;
            const stages = p.pipeline_stages || [];
            const currentIdx = stages.findIndex(s => s.key === p.current_stage);
            const progress = p.task_count ? Math.round((p.tasks_completed / p.task_count) * 100) : 0;
            return (
              <div key={p.id} className="card-flat p-5 hover:border-slate-700 cursor-pointer transition-all group"
                onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-${p.id}`}>
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base group-hover:text-indigo-400 transition-colors" style={{ color: '#F1F5F9' }}>{p.title}</h3>
                    {p.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{p.description}</p>}
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ml-2" style={{ backgroundColor: badge.bg, color: badge.color }}>{badge.label}</span>
                </div>

                {/* Pipeline Progress */}
                <div className="flex items-center gap-0.5 mb-3">
                  {stages.map((stage, i) => (
                    <div key={stage.key} className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: i <= currentIdx ? stage.color : '#e2e8f0' }} title={stage.label} />
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mb-3">
                  Stage: <span className="font-medium text-slate-300">{stages[currentIdx]?.label || p.current_stage}</span>
                </p>

                {/* Stats */}
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><CheckCircle size={12} className="text-indigo-400" /> {p.tasks_completed}/{p.task_count} tasks</span>
                  <span className="flex items-center gap-1"><Target size={12} className="text-violet-500" /> {p.benchmarks_met}/{p.benchmarks_total} benchmarks</span>
                  {p.team?.length > 0 && <span className="flex items-center gap-1"><Users size={12} /> {p.team.length}</span>}
                </div>

                {/* Task progress bar */}
                {p.task_count > 0 && (
                  <div className="mt-3 w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-900/300 rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                )}

                {/* Tags */}
                {p.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {p.tags.slice(0, 4).map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded">{t}</span>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-project-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-section">New Project</h2>
              <button onClick={() => setShowCreate(false)}><XCircle size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <input className="input-field w-full" placeholder="Project title *" required value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} data-testid="project-title-input" />
              <textarea className="input-field w-full" rows={3} placeholder="Description" value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Start Date</label>
                  <input type="date" className="input-field w-full" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Target End Date</label>
                  <input type="date" className="input-field w-full" value={form.target_end_date} onChange={e => setForm(p => ({...p, target_end_date: e.target.value}))} />
                </div>
              </div>
              <input className="input-field w-full" placeholder="Tags (comma-separated)" value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} />
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Team Members (name:role, comma-separated)</label>
                <input className="input-field w-full" placeholder="e.g., Alice:lead, Bob:contributor" value={teamInput} onChange={e => setTeamInput(e.target.value)} />
              </div>
              <Button type="submit" className="btn-primary w-full" data-testid="submit-project-btn">Create Project</Button>
            </form>
          </div>
        </div>
      )}
      {showImport && <ImportModal type="projects" onClose={() => setShowImport(false)} onImportComplete={load} />}
    </div>
  );
};

export default ProjectsPage;
